import { useState, useEffect } from "react";
import { Container, Table, Badge, Button, Modal, Form, Image } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaEdit, FaTrash } from "react-icons/fa";

import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";
import { getCategories } from "../../api/api";

const ADMIN_API_BASE = "https://admin.soulliqo.com";

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [show, setShow] = useState(false);
  const [editData, setEditData] = useState(null);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Delete category locally
  const handleDelete = (id) => {
    setCategories(categories.filter((item) => item.id !== id));
  };

  // Edit category
  const handleEdit = (category) => {
    const imageString = Array.isArray(category.image)
      ? category.image.join(",")
      : category.image || "";
    setEditData({ ...category, image: imageString });
    setShow(true);
  };

  // Save edited category
  const handleSave = () => {
    const updatedCategory = {
      ...editData,
      image: editData.image.split(",").map((img) => img.trim()).filter(Boolean),
    };
    setCategories(
      categories.map((item) => (item.id === editData.id ? updatedCategory : item))
    );
    setShow(false);
  };

  // Normalize images from admin
  const normalizeImages = (imageInput) => {
    if (!imageInput) return [];
    const rawArray = Array.isArray(imageInput)
      ? imageInput
      : imageInput.split(",").map((img) => img.trim()).filter(Boolean);

    return rawArray.map((img) => {
      if (img.startsWith("http")) return img;
      const cleanPath = img.startsWith("/") ? img.substring(1) : img;
      if (cleanPath.startsWith("images/")) return `/${cleanPath}`;
      return `${ADMIN_API_BASE}/${cleanPath}`;
    });
  };

  return (
    <div>
      <Header />
      <Sidebar />
      <div className="admin-layout">
        <main className="admin-content">
          <Container fluid>
            <motion.h4 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-4">
              Categories
            </motion.h4>

            <Table responsive bordered hover className="category-table shadow-sm bg-white">
              <thead className="bg-light">
                <tr>
                  <th>Category Name</th>
                  <th>Image Gallery</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((item, index) => {
                  const images = normalizeImages(item.image);
                  return (
                    <tr key={item.id || index}>
                      <td>{item.name}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          {images.length > 0 ? (
                            images.map((img, idx) => (
                              <Image
                                key={idx}
                                src={img}
                                alt={item.name}
                                rounded
                                style={{
                                  width: "70px",
                                  height: "70px",
                                  objectFit: "cover",
                                  border: "1px solid #eee",
                                }}
                                onError={(e) => {
                                  e.target.src = "https://via.placeholder.com/70?text=Error";
                                }}
                              />
                            ))
                          ) : (
                            <small className="text-muted">No Images</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge bg={item.status === "Active" ? "success" : "secondary"}>
                          {item.status}
                        </Badge>
                      </td>
                      <td>
                        <Button size="sm" variant="link" onClick={() => handleEdit(item)}>
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          className="text-danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Container>
        </main>
      </div>

      {/* Edit Category Modal */}
      <Modal show={show} onHide={() => setShow(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Category</Modal.Title>
        </Modal.Header>
        {editData && (
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Image Paths (Use commas to separate)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editData.image}
                  onChange={(e) => setEditData({ ...editData, image: e.target.value })}
                />
                <div className="mt-3 p-2 bg-light border rounded">
                  <small className="d-block mb-2 text-primary font-weight-bold">Live Preview:</small>
                  <div className="d-flex flex-wrap gap-2">
                    {normalizeImages(editData.image).map((img, idx) => (
                      <Image
                        key={idx}
                        src={img}
                        rounded
                        style={{ height: "60px", width: "60px", objectFit: "cover" }}
                        onError={(e) => { e.target.src = "https://via.placeholder.com/60?text=Error"; }}
                      />
                    ))}
                  </div>
                </div>
              </Form.Group>

              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                >
                  <option>Active</option>
                  <option>Inactive</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="light" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Categories;
